import { useEffect, useState, type FormEvent } from "react";
import { Fingerprint, KeyRound, LoaderCircle } from "lucide-react";
import type { DeviceIdentity, Session } from "../types";
import { appLockPreference, loadIdentity, loadIdentityAutomatic, loadIdentityWithSystem, saveIdentity, saveIdentityAutomatic, vaultProtection, type VaultProtection } from "../vault";

type Props = Readonly<{
  session: Session;
  onLogout: () => Promise<void>;
  onUnlocked: (identity: DeviceIdentity, protection: Exclude<VaultProtection, "none">) => void;
}>;

export function UnlockScreen({ session, onLogout, onUnlocked }: Props) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [protection, setProtection] = useState<"loading" | VaultProtection>("loading");

  useEffect(() => {
    let cancelled = false;
    void vaultProtection(session.user.username)
      .then(async (value) => {
        if (cancelled) return;
        setProtection(value);
        if (value !== "automatic") return;
        setBusy(true);
        setError(null);
        try {
          const loaded = await loadIdentityAutomatic(session.user.username);
          if (!cancelled) onUnlocked(loaded.id === session.deviceId ? loaded : { ...loaded, id: session.deviceId }, "automatic");
        } catch (caught) {
          if (!cancelled) setError(caught instanceof Error ? caught.message : "Не удалось автоматически открыть локальные ключи");
        } finally {
          if (!cancelled) setBusy(false);
        }
      })
      .catch((caught) => { if (!cancelled) setError(caught instanceof Error ? caught.message : "Не удалось прочитать локальное хранилище"); });
    return () => { cancelled = true; };
  }, [onUnlocked, session.deviceId, session.user.username]);

  const finish = (loaded: DeviceIdentity, activeProtection: Exclude<VaultProtection, "none">) => {
    onUnlocked(loaded.id === session.deviceId ? loaded : { ...loaded, id: session.deviceId }, activeProtection);
  };

  const unlockWithSystem = async () => {
    setBusy(true);
    setError(null);
    try {
      const loaded = await loadIdentityWithSystem(session.user.username);
      const identity = loaded.id === session.deviceId ? loaded : { ...loaded, id: session.deviceId };
      if (appLockPreference(session.user.username) === null) {
        await saveIdentityAutomatic(session.user.username, identity);
        finish(identity, "automatic");
      } else {
        finish(identity, "system");
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Не удалось открыть ключи через системную защиту");
    } finally {
      setBusy(false);
    }
  };

  const unlockAutomatically = async () => {
    setBusy(true);
    setError(null);
    try {
      finish(await loadIdentityAutomatic(session.user.username), "automatic");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Не удалось автоматически открыть локальные ключи");
    } finally {
      setBusy(false);
    }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const loaded = await loadIdentity(session.user.username, password);
      const identity: DeviceIdentity = loaded.id === session.deviceId ? loaded : { ...loaded, id: session.deviceId };
      if (appLockPreference(session.user.username) === null) {
        await saveIdentityAutomatic(session.user.username, identity);
        finish(identity, "automatic");
      } else {
        if (identity.id !== loaded.id) await saveIdentity(session.user.username, password, identity);
        finish(identity, "passphrase");
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Не удалось открыть ключи");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="unlock-page">
      <section className="unlock-card">
        <div className="unlock-icon">{protection === "system" ? <Fingerprint /> : protection === "automatic" ? <LoaderCircle className="spin-icon" /> : <KeyRound />}</div>
        <p className="eyebrow">ЗАЩИЩЁННОЕ УСТРОЙСТВО</p>
        <h1>{protection === "automatic" ? "Открываем аккаунт" : "Откройте локальные ключи"}</h1>
        <p>{protection === "automatic"
          ? <>Ключи <b>@{session.user.username}</b> открываются из защищённого хранилища этого устройства без дополнительного запроса.</>
          : protection === "system"
          ? <>Подтвердите вход в <b>@{session.user.username}</b> через Windows Hello, Touch ID, биометрию или системный PIN.</>
          : <>Сессия <b>@{session.user.username}</b> активна, но после перезагрузки приватные ключи нужно расшифровать заново.</>}</p>
        {protection === "automatic" ? (
          <div className="system-unlock-actions">
            {error && <div className="form-error" role="alert">{error}</div>}
            {error && <button className="primary-button" disabled={busy} onClick={() => void unlockAutomatically()} type="button">{busy ? "Открываем…" : "Повторить"}</button>}
          </div>
        ) : protection === "system" ? (
          <div className="system-unlock-actions">
            {error && <div className="form-error" role="alert">{error}</div>}
            <button className="primary-button" disabled={busy} onClick={() => void unlockWithSystem()} type="button"><Fingerprint />{busy ? "Проверяем…" : "Открыть через систему"}</button>
          </div>
        ) : (
          <form onSubmit={(event) => void submit(event)}>
            <label><span>Пароль или локальная защитная фраза</span><input autoFocus autoComplete="current-password" onChange={(event) => setPassword(event.target.value)} type="password" value={password} /></label>
            {error && <div className="form-error" role="alert">{error}</div>}
            <button className="primary-button" disabled={busy || protection === "loading"} type="submit">{busy ? "Открываем…" : protection === "loading" ? "Проверяем защиту…" : "Продолжить"}</button>
          </form>
        )}
        <button className="text-button" onClick={() => void onLogout()} type="button">Выйти из аккаунта</button>
      </section>
    </main>
  );
}
